import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ChevronLeft, CircleCheck, Upload } from "lucide-react";
import { api, type S } from "@/shared/api/client";
import { useAction } from "@/shared/lib/query";
import { useCan } from "@/shared/lib/session";
import { useImportKinds } from "@/shared/ui/ImportButton";
import { apps, hrefParts } from "./apps";
import { DemoButton } from "./Demo";

// Where the platform's own steps are done; each app says where its steps are done.
const coreSteps: Record<string, string> = { company_profile: "/settings/company", opening_balances: "/settings/opening-balances", customers: "/organizations",
  team: "/settings/users?invite=1", security: "/settings/security" };

function useSetup() {
  const can = useCan();
  return useQuery({ queryKey: ["/v1/core/setup"], queryFn: () => api.get<S["Setup"]>("/v1/core/setup"), enabled: can("core.apps.manage") });
}

/** "Start here": the first steps of the apps that are on, ticked off as the data appears.
 * Hiding it hides the steps of the apps on now; an app switched on later brings its own. */
export function SetupChecklist() {
  const { t } = useTranslation();
  const q = useSetup();
  const hide = useAction(() => api.post("/v1/core/setup/dismiss"), { invalidate: [["/v1/core/setup"]] });
  const imports = useImportKinds();
  const items = q.data?.items ?? [];
  const done = items.filter((i) => i.done).length;
  if (items.length === 0 || done === items.length) return null;
  const next = items.find((i) => !i.done);
  const where = (i: S["SetupItem"]) => (i.app === "core" ? coreSteps[i.key] : apps.find((a) => a.key === i.app)?.setup?.[i.key]);
  return (
    <section className="panel mb-5 p-5" aria-labelledby="setup-title">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="setup-title" className="text-[16px] font-semibold">{t("setup.title")}</h2>
        <span className="num text-[13px] text-ink-3">{t("setup.progress", { done, total: items.length })}</span>
        <span className="ms-auto flex flex-wrap items-center gap-1">
          {(imports.data?.length ?? 0) > 0 && (
            <Link to="/settings/import" className="btn-ghost py-1 text-[13px] text-ledger"><Upload className="size-4" /> {t("setup.import")}</Link>
          )}
          <DemoButton className="py-1 text-[13px] text-ledger" />
          <button type="button" className="btn-ghost py-1 text-[13px]" onClick={() => hide.mutate(undefined)}>{t("setup.hide")}</button>
        </span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-line-2" role="progressbar" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={done}>
        <div className="h-full rounded-full bg-ledger" style={{ width: `${(done / items.length) * 100}%` }} />
      </div>
      {/* Numbered and read down each column, in the order the steps depend on each other;
          the next one to do stands out. */}
      <ol className="mt-4 gap-x-6 md:columns-2">
        {items.map((i, n) => {
          const label = t(`setup.${i.app}.${i.key}`);
          const to = where(i);
          const isNext = i === next;
          const mark = i.done ? <CircleCheck className="mt-0.5 size-5 shrink-0 text-ledger" /> : (
            <span className={clsx("num mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border text-[11px]",
              isNext ? "border-ledger bg-ledger text-white" : "border-line text-ink-3")}>{n + 1}</span>
          );
          if (i.done || !to) {
            return (
              <li key={i.app + i.key} className="flex break-inside-avoid items-start gap-2 px-2 py-1.5">
                {mark}
                <span className={i.done ? "text-ink-3 line-through" : undefined}>{label}</span>
              </li>
            );
          }
          const { to: path, search } = hrefParts(to);
          return (
            <li key={i.app + i.key} className="break-inside-avoid">
              <Link to={path} search={search as never} className="group flex items-start gap-2 rounded-[var(--radius-ctl)] px-2 py-1.5 hover:bg-paper">
                {mark}
                <span className={clsx("flex-1", isNext && "font-medium")}>{label}</span>
                <ChevronLeft className="mt-0.5 size-4 shrink-0 text-ink-3 group-hover:text-ledger ltr:rotate-180" />
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
